"use client";

// @ts-strict-ignore
import React from "react";
import type { BlogArticlePageProps } from "types/blog-type";
import {
  ArrowLeftIcon,
  ArticleCategory,
  ArticleListContainer,
  ArticlePublishDate,
  AuthorContainer,
  AuthorDetails,
  AuthorImgContainer,
  AuthorNameOrTitle,
  BlogArticlePageBackground,
  BlogArticlePageContainer,
  BlogContent,
  BlogContentContainer,
  BlogHeroImgContainer,
  BlogHomepageLink,
  CategoryAndPublishedDate,
  Divider,
  MoreFromAuthorTitleBar,
  SocialIconsContainer
} from "./style";
import ArrowLeft from "public/images/blog/arrow-left.svg?inline";
import { Body, Heading } from "components/parts/Typography";
import SocialShareIcons from "components/parts/SocialShareIcons";
import NextLink from "components/parts/NextLink";
import Markdown from "components/parts/Markdown";
import TextLink from "components/parts/NextLink/TextLink";
import ArticleRow, { Separator } from "../ArticleRow";
import { Grid } from "components/layout/Grid";
import NotFound from "components/site/NotFound";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import Image from "next/image";
import { contentfulLoader } from "utils/images/contentfulLoader";
import { InlineCTAContext } from "utils/context";
import ContentfulInlineCTA from "components/modules/ContentfulInlineCTA";
import { useMixpanelTrackScrolling } from "utils/hooks";

const BlogArticlePage = ({
  blogPost,
  similarPosts,
  morePostsFromAuthor,
  notFound
}: BlogArticlePageProps) => {
  useMixpanelTrackScrolling({
    mixpanelEventName: "Scrolled on blog article",
    properties: {
      "article name": blogPost?.title ? blogPost.title : blogPost.slug
    }
  });
  const t = useTranslations(FILES.BLOG);
  if (notFound) return <NotFound data-testid="error" linkTo={"blog"} />;
  return (
    <BlogArticlePageBackground>
      <BlogArticlePageContainer>
        <BlogHomepageLink href="/blog">
          <ArrowLeftIcon src={ArrowLeft} alt="Return to blog home" />
          <Body size="md" m="0" ml="8px">
            {t("goHomeLink")}
          </Body>
        </BlogHomepageLink>

        <Divider />

        <CategoryAndPublishedDate>
          {blogPost.blogType[0] && (
            <NextLink
              href={`/blog/type/${blogPost.blogType[0].type.toLowerCase()}`}
            >
              <ArticleCategory mr="8px" forwardedAs="h3">
                {blogPost.blogType?.[0].type?.toUpperCase()}
              </ArticleCategory>
            </NextLink>
          )}
          {blogPost.datePublished && (
            <ArticlePublishDate>
              {blogPost.datePublished.toUpperCase()}
            </ArticlePublishDate>
          )}
        </CategoryAndPublishedDate>

        <Heading size="lg" mt="0" mb="16px" as="h1">
          {blogPost.title}
        </Heading>

        <SocialIconsContainer>
          <SocialShareIcons title={blogPost.title} />
        </SocialIconsContainer>

        {blogPost.blogHeroImage.url && (
          <BlogHeroImgContainer>
            <Image
              src={blogPost.blogHeroImage.url}
              alt={blogPost.blogHeroImage.alt}
              width={650}
              height={450}
              loader={contentfulLoader}
            />
          </BlogHeroImgContainer>
        )}

        {blogPost.blogAuthors[0] && (
          <AuthorContainer>
            <AuthorImgContainer>
              <Image
                src={blogPost.blogAuthors[0].profileImage.url}
                alt={blogPost.blogAuthors[0].profileImage.alt}
                width={48}
                height={48}
                loader={contentfulLoader}
                style={{ borderRadius: "50%" }}
              />
            </AuthorImgContainer>
            <NextLink href={`/blog/author/${blogPost.blogAuthors[0].slug}`}>
              <AuthorDetails>
                <AuthorNameOrTitle semiBold>
                  {blogPost.blogAuthors[0].authorName.toUpperCase()}
                </AuthorNameOrTitle>
                {blogPost.blogAuthors[0].authorPosition.length && (
                  <AuthorNameOrTitle m="0">
                    {blogPost.blogAuthors[0].authorPosition}
                  </AuthorNameOrTitle>
                )}
              </AuthorDetails>
            </NextLink>
          </AuthorContainer>
        )}

        <BlogContentContainer>
          <InlineCTAContext.Provider value={{ data: blogPost.cta }}>
            <BlogContent>
              <Markdown
                source={blogPost.blogPostContent}
                config={{ details: ContentfulInlineCTA }}
              />
            </BlogContent>
          </InlineCTAContext.Provider>
        </BlogContentContainer>

        {!!similarPosts.length && (
          <Grid>
            <Body size="md" semiBold m="0 !important" as="h2">
              {t("relatedArticlesTitle").toUpperCase()}
            </Body>
            <Separator />
            <ArticleListContainer>
              {similarPosts.map((post) => (
                <React.Fragment key={post.id}>
                  <ArticleRow
                    imageAlt={post.blogHeroImage.alt}
                    imageUrl={post.blogHeroImage.url}
                    slug={post.slug}
                    category={post.blogType[0].type}
                    datePublished={post.datePublished}
                    title={post.title}
                    keyIdx={similarPosts.indexOf(post)}
                  />
                  <Separator />
                </React.Fragment>
              ))}
            </ArticleListContainer>
          </Grid>
        )}

        {!!morePostsFromAuthor.length && (
          <React.Fragment>
            <MoreFromAuthorTitleBar>
              <Body size="md" semiBold m="0">
                {t("moreArticlesTitle", {
                  author: blogPost.blogAuthors[0].authorName
                }).toUpperCase()}
              </Body>
              <TextLink
                href={`/blog/author/[author]`}
                as={`/blog/author/${blogPost.blogAuthors[0].slug}`}
              >
                <Body size="md" m="0">
                  {t("viewAllLink")}
                </Body>
              </TextLink>
            </MoreFromAuthorTitleBar>
            <Separator />
            <ArticleListContainer>
              {morePostsFromAuthor.map((post) => (
                <React.Fragment key={post.id}>
                  {/* Use keyIdx because of key is not a prop warning */}
                  <ArticleRow
                    imageAlt={post.blogHeroImage.alt}
                    imageUrl={post.blogHeroImage.url}
                    slug={post.slug}
                    category={post.blogType[0].type}
                    datePublished={post.datePublished}
                    title={post.title}
                    keyIdx={similarPosts.indexOf(post)}
                  />
                  <Separator />
                </React.Fragment>
              ))}
            </ArticleListContainer>
          </React.Fragment>
        )}
      </BlogArticlePageContainer>
    </BlogArticlePageBackground>
  );
};

export default BlogArticlePage;
